import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

/**
 * Generic OAuth authorization-code display page.
 *
 * Some apps hosted on other channels (e.g. a Telegram bot) borrow this domain
 * as their OAuth redirect target because they have no web UI of their own. After
 * the user authorizes, the provider redirects here with `?code=...`; this page
 * simply surfaces that code so the user can copy it back to wherever they
 * started the connection. It is provider- and app-agnostic — no token exchange
 * happens here.
 */
const OAuthCodePage = () => {
  const [searchParams] = useSearchParams()
  const [copied, setCopied] = useState(false)

  const code = searchParams.get('code')
  const error = searchParams.get('error')

  const copy = async () => {
    if (!code) return
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow p-6 text-center space-y-4">
        {error ? (
          <>
            <h1 className="text-2xl font-bold text-gray-900">Authorization cancelled</h1>
            <p className="text-sm text-gray-600">
              The request was denied or cancelled ({error}). You can close this page and try
              the connection link again.
            </p>
          </>
        ) : code ? (
          <>
            <h1 className="text-2xl font-bold text-gray-900">You're almost done</h1>
            <p className="text-sm text-gray-600">
              Copy this code and paste it back where you started the connection.
            </p>
            <div className="font-mono text-sm break-all bg-gray-100 rounded-md p-3 select-all">
              {code}
            </div>
            <button
              onClick={copy}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-md py-2 transition-colors"
            >
              {copied ? 'Copied!' : 'Copy code'}
            </button>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-gray-900">No code found</h1>
            <p className="text-sm text-gray-600">
              This page expects an OAuth <code>code</code> in the URL. Open it via the
              connection link you were given.
            </p>
          </>
        )}
      </div>
    </div>
  )
}

export default OAuthCodePage
